import React from "react";
import { useActivitiesData } from "./ActivitiesDataContext";
import { withLayoutCommon, getLayoutCommonRootProps } from "@digitalopsdev/serviceops-docs-components/dist/blocks/LayoutCommonMixin";

type WeeklyStatMetric = "sessions" | "weeklyLoad" | "monotony" | "decision" | "strain";

const defaultValueClass = "text-xl font-bold";
const defaultLabelClass = "text-xs opacity-90";

const WeeklyStatTile = {
    fields: {
        metric: {
            type: "select" as const,
            options: [
                { label: "Sessions (7 days)", value: "sessions" },
                { label: "Weekly Load (PSS)", value: "weeklyLoad" },
                { label: "Monotony", value: "monotony" },
                { label: "Strain", value: "strain" },
                { label: "Weekly Decision (band)", value: "decision" },
            ],
        },
        label: { type: "text" as const },
        styleMode: {
            type: "radio" as const,
            options: [
                { label: "Static gradient (use colorClass)", value: "static" },
                { label: "Decision band gradient (auto)", value: "decisionBand" },
            ],
        },
        colorClass: { type: "text" as const },
        decimals: { type: "number" as const },
        containerClass: { type: "text" as const },
        valueClass: { type: "text" as const },
        labelClass: { type: "text" as const },
    },

    // Dynamic Fields: hide colorClass when using decision band auto gradient;
    // hide decimals for non-numeric metrics (decision)
    resolveFields: (data: any) => {
        const metric: WeeklyStatMetric = (data?.props?.metric ?? "sessions") as WeeklyStatMetric;
        const styleMode: "static" | "decisionBand" = (data?.props?.styleMode ?? "static") as any;

        const base = {
            metric: (WeeklyStatTile as any).fields.metric,
            label: (WeeklyStatTile as any).fields.label,
            styleMode: (WeeklyStatTile as any).fields.styleMode,
            containerClass: (WeeklyStatTile as any).fields.containerClass,
            valueClass: (WeeklyStatTile as any).fields.valueClass,
            labelClass: (WeeklyStatTile as any).fields.labelClass,
        } as Record<string, any>;

        if (styleMode !== "decisionBand") {
            base.colorClass = (WeeklyStatTile as any).fields.colorClass;
        }
        if (metric !== "decision") {
            base.decimals = (WeeklyStatTile as any).fields.decimals;
        }
        return base;
    },

    defaultProps: {
        metric: "sessions" as WeeklyStatMetric,
        label: "Sessions (7 days)",
        styleMode: "static",
        colorClass: "from-blue-500 to-blue-600",
        decimals: 0,
        containerClass: "rounded-lg text-white",
        valueClass: defaultValueClass,
        labelClass: defaultLabelClass,
    },

    inline: true as const,

    render: (props: any) => {
        const {
            metric,
            label,
            styleMode,
            colorClass,
            decimals,
            containerClass,
            valueClass,
            labelClass,
            puck,
        } = props as any;
        const { totalSessionsThisWeek, weekReport, helpers } = useActivitiesData();

        const m = (metric as WeeklyStatMetric) ?? "sessions";

        const defaultLabels: Record<WeeklyStatMetric, string> = {
            sessions: "Sessions (7 days)",
            weeklyLoad: "Weekly Load (PSS)",
            monotony: "Monotony",
            decision: "Weekly Decision",
            strain: "Strain",
        };
        const labelText =
            typeof label === "string" && label.trim().length > 0
                ? label
                : defaultLabels[m];

        const defaultDecimals: Record<WeeklyStatMetric, number> = {
            sessions: 0,
            weeklyLoad: 0,
            monotony: 2,
            decision: 0,
            strain: 0,
        };
        const dec =
            Number.isFinite(Number(decimals)) && decimals !== null && decimals !== undefined
                ? Number(decimals)
                : defaultDecimals[m];

        let valueNode: React.ReactNode = "—";
        switch (m) {
            case "sessions": {
                valueNode = totalSessionsThisWeek ?? 0;
                break;
            }
            case "weeklyLoad": {
                const v = weekReport?.weeklyLoad ?? 0;
                valueNode = v.toFixed(dec);
                break;
            }
            case "monotony": {
                const v = weekReport?.monotony ?? 0;
                valueNode = v.toFixed(dec);
                break;
            }
            case "strain": {
                const v = weekReport?.strain ?? 0;
                valueNode = v.toFixed(dec);
                break;
            }
            case "decision": {
                const band = helpers.bandLabel((weekReport?.decisionFlag ?? 0) as any);
                valueNode = band;
                break;
            }
        }

        let secondaryLabel: string | null = null;
        switch (m) {
            case "weeklyLoad":
                secondaryLabel = helpers.getWeeklyScoreZone(weekReport?.weeklyLoad ?? 0);
                break;
            case "monotony":
                secondaryLabel = helpers.getMonotonyLabel(weekReport?.monotony ?? 0);
                break;
            case "strain":
                secondaryLabel = helpers.getStrainLabel(weekReport?.strain ?? 0);
                break;
            default:
                break;
        }

        const bgClass =
            styleMode === "decisionBand"
                ? (() => {
                    if (!weekReport) {
                        return "bg-gradient-to-br from-slate-500 to-slate-600";
                    }
                    if (m === "decision") {
                        return helpers.gradientClass((weekReport.decisionFlag ?? 0) as any);
                    }
                    if (m === "strain") {
                        return helpers.gradientClass((weekReport.strainFlag ?? 0) as any);
                    }
                    if (m === "monotony") {
                        return helpers.gradientClass((weekReport.monoFlag ?? 0) as any);
                    }
                    // Fallback to decision band colouring
                    return helpers.gradientClass((weekReport.decisionFlag ?? 0) as any);
                })()
                : `bg-gradient-to-br ${typeof colorClass === "string" && colorClass
                    ? colorClass
                    : "from-slate-500 to-slate-600"
                }`;

        const container =
            typeof containerClass === "string" && containerClass
                ? containerClass
                : "rounded-lg text-white";
        const valueCls =
            typeof valueClass === "string" && valueClass
                ? valueClass
                : defaultValueClass;
        const labelCls =
            typeof labelClass === "string" && labelClass
                ? labelClass
                : defaultLabelClass;

        const rootProps = getLayoutCommonRootProps(props, { className: `${bgClass} ${container}`.trim() });
        return (
            <div ref={puck?.dragRef} {...rootProps}>
                <div className={valueCls}>{valueNode}</div>
                <div className={labelCls} style={{ textWrap: "balance" }}>
                    {labelText}
                    {secondaryLabel ? (
                        <>
                            {" "}
                            · <span>{secondaryLabel}</span>
                        </>
                    ) : null}
                </div>
            </div>
        );
    },
};

export default withLayoutCommon(WeeklyStatTile);